import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { ChartData } from '@/types/renewalDashboard';
import { chartTheme } from '@/components/dashboard/chartTheme';
import { PRIMARY_CHART_COLORS } from '@/components/dashboard/colorUtils';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface RenewalChartsSectionProps {
  smsSuccessChart: ChartData | null;
  expiryDistributionChart: ChartData | null;
  monthTrendChart: ChartData | null;
  loading?: boolean;
  error?: string | null;
}

const RenewalChartsSection: React.FC<RenewalChartsSectionProps> = ({
  smsSuccessChart,
  expiryDistributionChart,
  monthTrendChart,
  loading = false,
  error = null
}) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} sm={12} md={6} lg={4} key={item}>
            <Card 
              sx={{ 
                boxShadow: 3,
                borderRadius: 2,
                height: '100%'
              }}
            >
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading chart...
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  // Transform ChartData to Nivo Pie format
  const transformPieData = (data: ChartData | null, colors?: string[]): Array<{ id: string; label: string; value: number; color?: string }> => {
    if (!data || !data.labels || !data.values) {
      return [];
    }

    return data.labels.map((label, index) => ({
      id: label,
      label: label,
      value: data.values[index],
      color: colors ? colors[index] : PRIMARY_CHART_COLORS[index % PRIMARY_CHART_COLORS.length]
    }));
  };

  // Transform ChartData to Nivo Bar format
  const transformBarData = (data: ChartData | null, colors?: string[], labelMap?: Record<string, string>): Array<{ [key: string]: string | number }> => {
    if (!data || !data.labels || !data.values) {
      return [];
    }

    return data.labels.map((label, index) => {
      // Apply label mapping if provided
      const mappedLabel = labelMap && labelMap[label] ? labelMap[label] : label;
      const item: { [key: string]: string | number } = {
        label: mappedLabel,
        value: data.values[index]
      };
      if (colors && colors[index]) {
        item.color = colors[index];
      }
      return item;
    });
  };

  // SMS Success Rate Pie Chart
  const smsPieData = transformPieData(smsSuccessChart, ['#28a745', '#dc3545']);
  const smsTotal = smsSuccessChart?.values?.reduce((sum, val) => sum + val, 0) || 0;

  // Expiry Distribution Bar Chart
  const expiryLabelMap: Record<string, string> = {
    '>1 Month': 'After 1 Month'
  };
  const expiryBarData = transformBarData(expiryDistributionChart, ['#ff6b6b', '#ffa726', '#ffeb3b', '#66bb6a', '#42a5f5'], expiryLabelMap);
  const expiryColors = ['#ff6b6b', '#ffa726', '#ffeb3b', '#66bb6a', '#42a5f5'];
  
  // Calculate max value for expiry chart to determine y-axis ticks
  const expiryMaxValue = expiryDistributionChart?.values 
    ? Math.max(...expiryDistributionChart.values) 
    : 0;
  
  // Generate clean y-axis tick values (50, 100, 150, etc.)
  const getExpiryTickValues = (maxValue: number): number[] => {
    if (maxValue === 0) return [0, 50, 100, 150, 200];
    const roundedMax = Math.ceil(maxValue / 50) * 50;
    const ticks: number[] = [];
    for (let i = 0; i <= roundedMax; i += 50) {
      ticks.push(i);
    }
    return ticks;
  };

  // Month-wise Trend Bar Chart
  const monthBarData = transformBarData(monthTrendChart);
  
  // Calculate max value for month trend chart to determine x-axis ticks
  const monthMaxValue = monthTrendChart?.values 
    ? Math.max(...monthTrendChart.values) 
    : 0;
  
  // Generate clean x-axis tick values (500, 1000, 1500, 2000, etc.)
  const getMonthTickValues = (maxValue: number): number[] => {
    if (maxValue === 0) return [0, 500, 1000, 1500, 2000];
    const roundedMax = Math.ceil(maxValue / 500) * 500;
    const ticks: number[] = [];
    for (let i = 0; i <= roundedMax; i += 500) {
      ticks.push(i);
    }
    return ticks;
  };

  return (
    <Grid container spacing={2}>
      {/* SMS Success Rate Pie Chart */}
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <Card 
          sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardHeader
            avatar={<PieChartIcon color="success" sx={{ fontSize: 28 }} />}
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                SMS Success Rate
              </Typography>
            }
            sx={{ pb: 0.5 }}
          />
          <CardContent sx={{ p: 1.5 }}>
            {smsPieData.length === 0 || smsTotal === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, height: 280 }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsivePie
                  data={smsPieData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  innerRadius={0.5}
                  padAngle={2}
                  cornerRadius={4}
                  activeOuterRadiusOffset={8}
                  colors={['#28a745', '#dc3545']}
                  borderWidth={2}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor="#ffffff"
                  arcLabel={(d) => {
                    const percentage = smsTotal > 0 ? Math.round((d.value / smsTotal) * 100) : 0;
                    return `${percentage}%`;
                  }}
                  theme={chartTheme}
                  tooltip={({ datum }) => (
                    <Box
                      sx={{
                        background: 'white',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: datum.color,
                            borderRadius: '2px'
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {datum.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Count: {datum.value.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Percentage: {smsTotal > 0 ? Math.round((datum.value / smsTotal) * 100) : 0}%
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Expiry Distribution Bar Chart */}
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <Card 
          sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardHeader
            avatar={<BarChartIcon color="primary" sx={{ fontSize: 28 }} />}
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Expiry Distribution
              </Typography>
            }
            sx={{ pb: 0.5 }}
          />
          <CardContent sx={{ p: 1.5 }}>
            {expiryBarData.length === 0 || expiryMaxValue === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, height: 280 }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveBar
                  data={expiryBarData}
                  keys={['value']}
                  indexBy="label"
                  margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                  padding={0.3}
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={({ index }) => expiryColors[index % expiryColors.length]}
                  theme={chartTheme}
                  borderRadius={4}
                  borderWidth={0}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 10,
                    tickRotation: -45,
                    legend: '',
                    legendPosition: 'middle',
                    legendOffset: 50
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    tickValues: getExpiryTickValues(expiryMaxValue),
                    legend: 'Count',
                    legendPosition: 'middle',
                    legendOffset: -40
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor="#ffffff"
                  tooltip={({ indexValue, value }) => (
                    <Box
                      sx={{
                        background: 'white',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {indexValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Count: {value?.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Month-wise Trend Bar Chart */}
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <Card 
          sx={{ 
            height: '100%',
            boxShadow: 3,
            borderRadius: 2,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardHeader
            avatar={<TrendingUpIcon color="info" sx={{ fontSize: 28 }} />}
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Month-wise Trend
              </Typography>
            }
            sx={{ pb: 0.5 }}
          />
          <CardContent sx={{ p: 1.5 }}>
            {monthBarData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, height: 280 }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveBar
                  data={monthBarData}
                  keys={['value']}
                  indexBy="label"
                  margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                  padding={0.3}
                  layout="vertical"
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={[PRIMARY_CHART_COLORS[0]]}
                  theme={chartTheme}
                  borderRadius={4}
                  borderWidth={0}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: '',
                    legendPosition: 'middle',
                    legendOffset: 50
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    tickValues: getMonthTickValues(monthMaxValue),
                    legend: 'Count',
                    legendPosition: 'middle',
                    legendOffset: -50
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor="#ffffff"
                  tooltip={({ indexValue, value }) => (
                    <Box
                      sx={{
                        background: 'white',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {indexValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Policies: {value?.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RenewalChartsSection;
